import React, { useState } from "react";
import { Form, Button, Input, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { AuthLayout } from "@/shared/layout";
import { userService } from "@/api/user";
import { normalizeEmail } from "@/shared/utils/email";

const StyledForm = styled(Form)`
	height: auto;

	.ant-form-item {
		margin-bottom: 1.5rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 47px;
		border-radius: 50px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 0.9rem;
	}
`;

const StepTransition = styled(motion.div)`
	width: 100%;
`;

const ContinueButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.PRIMARY};
	border: none;
	width: 100%;

	&:hover {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		opacity: 0.9;
	}
	&:disabled {
		background: #ebebe4 !important;
		opacity: 0.9;
	}
`;

const ErrorMessage = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 12px 14px;
	border-radius: 8px;
	background: #fff1f0;
	border: 1px solid #ffa39e;
	color: #a8071a;
	margin-bottom: 16px;

	.error-icon {
		font-size: 18px;
		color: #cf1322;
		margin-top: 2px;
	}

	.error-content {
		display: flex;
		flex-direction: column;
	}

	.error-title {
		font-weight: 600;
		margin-bottom: 2px;
	}

	.error-message {
		font-size: 13px;
	}
`;

const LoginLink = styled(Link)`
	color: var(--antd-primary);
	font-size: 0.875rem;
	text-decoration: none;

	&:hover {
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

const SuccessIconWrapper = styled.div`
	display: flex;
	justify-content: center;
	margin-bottom: 1rem;

	.icon-circle {
		width: 64px;
		height: 64px;
		border-radius: 50%;
		background: ${CUSTOM_COLORS.PRIMARY}1A;
		display: flex;
		align-items: center;
		justify-content: center;
		color: ${CUSTOM_COLORS.PRIMARY};
		font-size: 28px;
	}
`;

type Step = "email" | "sent";

const ForgotPassword: React.FC = () => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [step, setStep] = useState<Step>("email");
	const [email, setEmail] = useState("");
	const [isEmailValid, setIsEmailValid] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [resending, setResending] = useState(false);

	const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setEmail(value);
		setIsEmailValid(validateEmail(value));
	};

	const emailSuffix = email ? isEmailValid ? <CheckCircleOutlined style={{ color: "#52c41a" }} /> : null : null;

	const sendRecoveryEmail = async (targetEmail: string) => {
		await userService.recoverPassword(targetEmail);
	};

	const onFinish = async (values: unknown) => {
		setErrorMessage("");
		setLoading(true);
		const targetEmail = normalizeEmail((values as { email?: string })?.email);
		setEmail(targetEmail);
		try {
			await sendRecoveryEmail(targetEmail);
			setStep("sent");
		} catch (error: any) {
			const errMsg =
				error?.response?.data?.message || error?.message || "Failed to send recovery email. Please try again.";
			setErrorMessage(errMsg);
		} finally {
			setLoading(false);
		}
	};

	const handleResend = async () => {
		setErrorMessage("");
		setResending(true);
		try {
			await sendRecoveryEmail(email);
			message.success("Recovery email sent again. Please check your inbox.");
		} catch (error: any) {
			const errMsg =
				error?.response?.data?.message ||
				error?.message ||
				"Failed to resend recovery email. Please try again.";
			setErrorMessage(errMsg);
		} finally {
			setResending(false);
		}
	};

	const handleUseDifferentEmail = () => {
		setStep("email");
		setErrorMessage("");
	};

	const continueDisabled = !isEmailValid || loading;

	return (
		<AuthLayout
			title={step === "email" ? "Forgot password?" : ""}
			subtitle={
				step === "email"
					? "Enter the email associated with your account and we'll send you a link to reset your password."
					: ""
			}
			backLinkTo="/login"
		>
			<div style={{ display: "block", minHeight: errorMessage === "" ? 0 : 100 }}>
				<AnimatePresence mode="wait" initial={false}>
					{!!errorMessage && (
						<StepTransition
							key="forgot-error"
							initial={{ opacity: 0, y: -6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
						>
							<ErrorMessage>
								<CloseCircleOutlined className="error-icon" />
								<div className="error-content">
									<div className="error-title">Error</div>
									<div className="error-message">{errorMessage}</div>
								</div>
							</ErrorMessage>
						</StepTransition>
					)}
				</AnimatePresence>

				<AnimatePresence mode="wait" initial={false}>
					{step === "sent" && (
						<StepTransition
							key="forgot-sent-header"
							initial={{ opacity: 0, y: -6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
						>
							<div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
								<SuccessIconWrapper>
									<div className="icon-circle">
										<MailOutlined />
									</div>
								</SuccessIconWrapper>
								<h2
									style={{
										margin: "0 0 0.75rem 0",
										fontSize: "24px",
										fontWeight: 600,
										color: "#000",
									}}
								>
									Check your email
								</h2>
								<p style={{ lineHeight: "20px", margin: 0, color: "#555" }}>
									We sent a password reset link to
								</p>
								<p style={{ lineHeight: "20px", margin: 0, fontWeight: 500 }}>{email}</p>
								<p
									style={{
										marginTop: "0.75rem",
										fontSize: "0.85rem",
										color: "#888",
									}}
								>
									The link will expire soon.
								</p>
							</div>
						</StepTransition>
					)}
				</AnimatePresence>
			</div>

			<StyledForm form={form} name="forgotPassword" onFinish={onFinish} layout="vertical">
				<AnimatePresence mode="wait" initial={false}>
					<StepTransition
						key={`forgot-step-${step}`}
						initial={{ opacity: 0, x: 18 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -12 }}
						transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
					>
						{step === "email" && (
							<>
								<Form.Item
									name="email"
									label="Email"
									style={{ marginTop: "1.5rem", marginBottom: "2rem" }}
									rules={[
										{ required: true, message: "Please input your email!" },
										{ type: "email", message: "Please enter a valid email!" },
									]}
								>
									<Input
										prefix={<UserOutlined />}
										placeholder="Enter your email"
										size="large"
										onChange={handleEmailChange}
										suffix={emailSuffix}
									/>
								</Form.Item>
								<Form.Item>
									<ContinueButton
										type="primary"
										htmlType="submit"
										loading={loading}
										disabled={continueDisabled}
									>
										Send reset link
									</ContinueButton>
								</Form.Item>
							</>
						)}

						{step === "sent" && (
							<div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
								<ContinueButton
									type="primary"
									htmlType="button"
									loading={resending}
									onClick={handleResend}
								>
									Resend email
								</ContinueButton>
								<Button
									type="default"
									htmlType="button"
									onClick={handleUseDifferentEmail}
									style={{ height: 36, borderRadius: 20, fontSize: "0.9rem" }}
								>
									Use a different email
								</Button>
								<Button
									type="link"
									htmlType="button"
									onClick={() => navigate("/login")}
									style={{ fontSize: "0.875rem" }}
								>
									Back to log in
								</Button>
							</div>
						)}
					</StepTransition>
				</AnimatePresence>

				{step === "email" && (
					<div
						style={{
							textAlign: "center",
							marginTop: "1rem",
							marginBottom: "4rem",
							fontSize: "0.875rem",
						}}
					>
						Remember your password? <LoginLink to="/login">Log in</LoginLink>
					</div>
				)}
			</StyledForm>
		</AuthLayout>
	);
};

export default ForgotPassword;
